import type { CSSProperties, FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { ConsoleIdentity, ConsoleSwitcherEntry } from '@/lib/community/console-guard'
import type { ConsoleSectionKey } from '@/lib/community/console'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import {
    ArrowUpRight,
    Check,
    ChevronsUpDown,
    LayoutDashboard,
    Loader2,
    Package,
    ScrollText,
    Settings,
    ShieldCheck,
    Store,
    Tags,
    UserCog,
    Users
} from 'lucide-react'

import { cn } from '@/lib/utils'
import { consolePath } from '@/lib/community/console'
import { NAV_ROW } from '@/components/admin/nav-row'
import { TelegramGlyph } from '@/components/marketplace/telegram-glyph'

import { TooltipProvider } from '@/components/ui/tooltip'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'

import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarInset,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider,
    SidebarRail,
    useSidebar
} from '@/components/ui/sidebar'

/**
 * The Community Console's shell.
 *
 * It is a SEPARATE navigation from the platform console on purpose. A community
 * operator runs one marketplace: there is no directory of communities here, no
 * platform users, no global settings, no platform revenue and no platform audit
 * administration, and nothing in this file can render one — the entries come
 * from `identity.sections`, which the server built from
 * `visibleConsoleSections`. A person who holds both authorities reaches both
 * products, but through their own separately authorized surface.
 *
 * The row styling is shared with the platform sidebar through
 * `@/components/admin/nav-row`, so the two look like one application while
 * staying two navigations.
 */

const ICONS: Record<ConsoleSectionKey, LucideIcon | FC<{ className?: string }>> = {
    overview: LayoutDashboard,
    listings: Package,
    sellers: Users,
    moderation: ShieldCheck,
    categories: Tags,
    rules: ScrollText,
    telegram: TelegramGlyph,
    team: UserCog,
    settings: Settings
}

const ROLE_LABEL: Record<string, string> = {
    owner: 'Owner',
    admin: 'Admin',
    moderator: 'Moderator',
    listing_manager: 'Listings',
    analyst: 'Analyst',
    member: 'Member'
}

/** Up to two initials, for a marketplace with no mark of its own. */
const initialsFor = (name: string): string => name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'M'

/* ------------------------------------------------------------------ *
 *  Marketplace mark
 * ------------------------------------------------------------------ */

/**
 * A marketplace's own logo, falling back to its initials.
 *
 * `AvatarImage` only paints once the file actually loads, so a broken or
 * missing URL degrades to the initials rather than to an empty square. It is
 * decorative in every position it is used — the marketplace name is always
 * rendered as real text beside it — so it carries no alternative text.
 */
const MarketplaceMark: FC<{ name: string, logoUrl: string | null, className?: string }> = ({
    name,
    logoUrl,
    className
}): ReactNode => (
    <Avatar className={cn('size-7 shrink-0 rounded-adm-sm', className)} aria-hidden='true'>
        {logoUrl && <AvatarImage src={logoUrl} alt='' className='object-cover' />}

        <AvatarFallback className='rounded-adm-sm bg-adm-nav-active text-[10.5px] font-semibold text-adm-nav-ink'>
            {initialsFor(name)}
        </AvatarFallback>
    </Avatar>
)

/* ------------------------------------------------------------------ *
 *  Community switcher
 * ------------------------------------------------------------------ */

interface BrandProps {
    identity: ConsoleIdentity
    onNavigate: () => void
    rail: boolean
    /** The marketplace being navigated to, while that navigation is in flight. */
    pending: ConsoleSwitcherEntry | null
}

/**
 * Which marketplace you are in, and the way out to another one.
 *
 * The list is only ever what the server said this identity may open, and
 * selecting an entry is a full navigation to that community's own URL — so the
 * next request resolves its context and membership from scratch. Nothing about
 * the switch is trusted from the browser: picking a marketplace you no longer
 * belong to simply lands on a 404.
 */
const Brand: FC<BrandProps> = ({ identity, onNavigate, rail, pending }): ReactNode => {
    const others: Array<ConsoleSwitcherEntry> = identity.switcher.filter((entry) => entry.id !== identity.communityId)
    const single = others.length === 0

    // While a switch is in flight the header names the marketplace being moved
    // TO, not the one still rendered underneath. Naming the old one for the
    // second or two the server takes is the "stale marketplace" bug in
    // miniature, just in the one place a user is looking hardest.
    const shownName = pending?.name ?? identity.communityName
    const shownLogo = pending ? pending.logoUrl : identity.communityLogoUrl

    const mark = pending
        ? (
            <span
                aria-hidden='true'
                className='flex size-7 shrink-0 items-center justify-center rounded-adm-sm bg-adm-nav-active'
            >
                <Loader2 className='size-3.5 animate-spin text-adm-nav-muted motion-reduce:animate-none' />
            </span>
        )
        : <MarketplaceMark name={shownName} logoUrl={shownLogo} />

    const label = (
        <span className='min-w-0 flex-1 text-left'>
            <span className='block truncate text-[13px] font-semibold leading-tight tracking-[-0.01em] text-adm-nav-ink'>
                {shownName}
            </span>

            <span className='mt-0.5 block truncate text-[11px] leading-tight text-adm-nav-muted'>
                {pending
                    ? 'Switching…'
                    : identity.viaPlatform
                        ? 'Platform access'
                        : ROLE_LABEL[identity.role ?? 'member'] ?? 'Member'}
            </span>
        </span>
    )

    // One marketplace, no menu. Nothing to choose between, so the header is a
    // plain link home rather than a control that opens onto a single item.
    if (single) {
        return (
            <Link
                href={consolePath(identity.communityId)}
                onClick={onNavigate}
                aria-label={`${identity.communityName} console`}
                className={cn(
                    'adm-focus flex min-h-11 min-w-0 items-center gap-2.5 rounded-adm-nav px-1.5 py-1.5 transition-colors duration-150 hover:bg-adm-nav-hover motion-reduce:transition-none',
                    rail ? 'justify-center' : 'flex-1'
                )}
            >
                {mark}
                {!rail && label}
            </Link>
        )
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                render={(
                    <button
                        type='button'
                        aria-label={`Switch marketplace. Currently ${identity.communityName}`}
                        className={cn(
                            'adm-focus flex min-h-11 min-w-0 items-center gap-2.5 rounded-adm-nav px-1.5 py-1.5 transition-colors duration-150 hover:bg-adm-nav-hover motion-reduce:transition-none data-open:bg-adm-nav-active',
                            rail ? 'justify-center' : 'flex-1'
                        )}
                    />
                )}
            >
                {mark}

                {!rail && (
                    <>
                        {label}

                        <ChevronsUpDown className='size-4 shrink-0 text-adm-nav-muted' aria-hidden='true' />
                    </>
                )}
            </DropdownMenuTrigger>

            <DropdownMenuContent
                align='start'
                sideOffset={8}
                className='w-64 max-w-[calc(100vw-2rem)] rounded-adm-lg border-adm-nav-line bg-adm-nav-surface p-1 shadow-adm-pop'
            >
                {/*
                    THE GROUP IS REQUIRED, NOT DECORATION.

                    `DropdownMenuLabel` renders Base UI's `Menu.GroupLabel`,
                    which reads `MenuGroupContext` to wire its `aria-labelledby`
                    onto the group it names. Base UI throws when that context is
                    absent — `MenuGroupContext is missing. Menu group parts must
                    be used within <Menu.Group> or <Menu.RadioGroup>`, minified
                    to error 31 in production.

                    Rendered bare inside the popup, that threw the moment the
                    menu opened, which took the whole console down with a
                    client-side exception rather than merely dropping an
                    attribute. It is why clicking the marketplace switcher
                    crashed the app.

                    The label genuinely does name these items, so the group is
                    also the correct semantics: screen readers now announce the
                    list as "Your marketplaces" instead of a set of loose items.
                */}
                <DropdownMenuGroup>
                    <DropdownMenuLabel className='px-2 py-1.5 text-[11px] font-medium uppercase tracking-[0.06em] text-adm-nav-muted'>
                        Your marketplaces
                    </DropdownMenuLabel>

                    <DropdownMenuSeparator className='bg-adm-nav-line' />

                    {identity.switcher.map((entry) => {
                        const current = entry.id === identity.communityId

                        return (
                            <DropdownMenuItem
                                key={entry.id}
                                render={<Link href={consolePath(entry.id)} onClick={onNavigate} />}
                                className='min-h-11 gap-2.5 rounded-adm px-2 py-1.5 text-[13px] text-adm-nav-muted focus:bg-adm-nav-hover focus:text-adm-nav-ink-strong'
                            >
                                <MarketplaceMark name={entry.name} logoUrl={entry.logoUrl} className='size-6' />

                                <span className='min-w-0 flex-1'>
                                    <span className='block truncate font-medium text-adm-nav-ink'>
                                        {entry.name}
                                    </span>

                                    <span className='block truncate text-[11.5px] text-adm-nav-muted'>
                                        {ROLE_LABEL[entry.role] ?? entry.role}
                                    </span>
                                </span>

                                {current && (
                                    <>
                                        <Check className='size-4 shrink-0 text-adm-nav-ink' aria-hidden='true' />

                                        <span className='sr-only'>
                                            Current marketplace
                                        </span>
                                    </>
                                )}
                            </DropdownMenuItem>
                        )
                    })}
                </DropdownMenuGroup>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

/* ------------------------------------------------------------------ *
 *  Sidebar
 * ------------------------------------------------------------------ */

const ConsoleSidebar: FC<{ identity: ConsoleIdentity, pending: ConsoleSwitcherEntry | null }> = ({
    identity,
    pending
}): ReactNode => {
    const { state, isMobile, setOpenMobile } = useSidebar()

    const rail = state === 'collapsed' && !isMobile

    const onNavigate = (): void => {
        if (isMobile) setOpenMobile(false)
    }

    const groups = identity.sections.reduce<Array<{ heading: string | null, keys: Array<ConsoleSectionKey> }>>(
        (accumulator, section) => {
            const last = accumulator[accumulator.length - 1]

            if (last && last.heading === section.heading) {
                last.keys.push(section.key)

                return accumulator
            }

            accumulator.push({ heading: section.heading, keys: [section.key] })

            return accumulator
        },
        []
    )

    const labelFor = (key: ConsoleSectionKey): string =>
        identity.sections.find((section) => section.key === key)?.label ?? key

    return (
        <Sidebar collapsible='icon' className='border-adm-nav-line bg-adm-nav-surface'>
            <SidebarHeader className='gap-0 bg-adm-nav-surface p-0'>
                <div className={cn('flex min-h-14 shrink-0 items-center gap-1 px-2', rail && 'justify-center px-0')}>
                    <Brand identity={identity} onNavigate={onNavigate} rail={rail} pending={pending} />
                </div>
            </SidebarHeader>

            <SidebarContent className='gap-4 bg-adm-nav-surface py-2'>
                {groups.map((group, index) => (
                    <SidebarGroup key={group.heading ?? `group-${index}`} className='gap-0.5 px-2 py-0'>
                        {group.heading && (
                            <SidebarGroupLabel className='h-7 px-2.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-adm-nav-muted'>
                                {group.heading}
                            </SidebarGroupLabel>
                        )}

                        <SidebarGroupContent>
                            <SidebarMenu className='gap-0.5'>
                                {group.keys.map((key) => {
                                    const Icon = ICONS[key]
                                    const active = identity.active === key
                                    const href = consolePath(identity.communityId, key)

                                    return (
                                        <SidebarMenuItem key={key}>
                                            <SidebarMenuButton
                                                isActive={active}
                                                tooltip={labelFor(key)}
                                                className={NAV_ROW}
                                                render={(
                                                    <Link
                                                        href={href}
                                                        onClick={onNavigate}
                                                        aria-current={active ? 'page' : undefined}
                                                    />
                                                )}
                                            >
                                                <Icon aria-hidden='true' />

                                                <span className='min-w-0 flex-1 truncate'>
                                                    {labelFor(key)}
                                                </span>
                                            </SidebarMenuButton>
                                        </SidebarMenuItem>
                                    )
                                })}
                            </SidebarMenu>
                        </SidebarGroupContent>
                    </SidebarGroup>
                ))}
            </SidebarContent>

            <SidebarFooter className='gap-2 bg-adm-nav-surface p-2'>
                <SidebarMenu className='gap-0.5'>
                    {/*
                        THIS marketplace's own storefront, resolved on the server
                        by `@/lib/community/storefront`. It was a literal `/`,
                        which handed every community operator the founding
                        marketplace instead of their own. It is absent rather
                        than dead while their storefront is not serving anyone —
                        awaiting review, suspended, switched off or private — and
                        the shell above already explains which of those it is.
                    */}
                    {identity.storefrontHref && (
                        <SidebarMenuItem>
                            <SidebarMenuButton
                                tooltip='View marketplace'
                                className={NAV_ROW}
                                render={(
                                    <Link
                                        href={identity.storefrontHref}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                    />
                                )}
                            >
                                <ArrowUpRight aria-hidden='true' />

                                <span className='min-w-0 flex-1 truncate'>
                                    View marketplace
                                </span>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    )}

                    {/*
                        The ONLY link between the two products, and it appears
                        only for someone the server already confirmed holds
                        platform authority. It is a way back, never a way in.
                    */}
                    {identity.viaPlatform && (
                        <SidebarMenuItem>
                            <SidebarMenuButton
                                tooltip='Platform console'
                                className={NAV_ROW}
                                render={<Link href='/admin/communities' onClick={onNavigate} />}
                            >
                                <Store aria-hidden='true' />

                                <span className='min-w-0 flex-1 truncate'>
                                    Platform console
                                </span>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    )}
                </SidebarMenu>

                {!rail && (
                    <div className='flex min-w-0 items-center gap-2.5 rounded-adm-lg border border-adm-nav-line bg-adm-nav-hover px-2 py-2'>
                        <Avatar className='size-7 shrink-0 rounded-full' aria-hidden='true'>
                            <AvatarFallback className='rounded-full bg-adm-nav-active text-[10.5px] font-semibold text-adm-nav-ink'>
                                {initialsFor(identity.actorName)}
                            </AvatarFallback>
                        </Avatar>

                        <div className='min-w-0 flex-1'>
                            <p className='truncate text-[12px] font-medium leading-tight text-adm-nav-ink'>
                                {identity.actorName}
                            </p>

                            <p className='mt-0.5 truncate text-[11px] leading-tight text-adm-nav-muted'>
                                {identity.viaPlatform
                                    ? 'Platform administrator'
                                    : ROLE_LABEL[identity.role ?? 'member'] ?? 'Member'}
                            </p>
                        </div>
                    </div>
                )}
            </SidebarFooter>

            <SidebarRail />
        </Sidebar>
    )
}

/* ------------------------------------------------------------------ *
 *  Layout
 * ------------------------------------------------------------------ */

const COOKIE = 'sidebar_state'

/** The community id a console URL addresses, or null if it is not one. */
const communityIdIn = (url: string): string | null =>
    /^\/admin\/community\/([^/?#]+)/.exec(url)?.[1] ?? null

/**
 * Mounted above the page component in `_app`, exactly as the platform shell is,
 * so moving between console pages swaps only the content column.
 */
export const ConsoleLayout: FC<{ identity: ConsoleIdentity, children: ReactNode }> = ({
    identity,
    children
}): ReactNode => {
    const router = useRouter()

    // Server-safe default, then the stored preference once mounted. Reading the
    // cookie during render would diverge between server and client.
    const [open, setOpen] = useState(true)

    /*
        SWITCHING MARKETPLACE IS A SERVER NAVIGATION, AND THAT IS THE PROBLEM
        THIS SOLVES.

        Every console page resolves its tenant in `getServerSideProps`, so
        picking another marketplace takes a round trip. Next.js keeps the
        CURRENT page mounted for the whole of it — which means the previous
        marketplace's listings, counts, team and Telegram settings stay on
        screen, under a URL that already names the new one. That is precisely
        the "stale tenant data during switch" this has to rule out, and no
        amount of care inside a page can fix it, because the page has not been
        asked for the new data yet.

        So the shell covers its own content column for the duration. The old
        page is made `inert`, so nothing under the cover is focusable,
        clickable or reachable by a screen reader, and a plain progress panel
        names where we are going. It clears on `routeChangeComplete` — by which
        point `_app` has remounted the page under its new community key, so
        what appears underneath is already the new marketplace's own data.

        Rapid switching is safe by construction: `routeChangeStart` simply
        overwrites the target, Next cancels the superseded navigation, and the
        single completion event clears the cover once.
    */
    const [pendingId, setPendingId] = useState<string | null>(null)

    useEffect(() => {
        const start = (url: string): void => {
            const next = communityIdIn(url)

            // Moving WITHIN this marketplace is an ordinary page change and
            // keeps the existing hairline progress bar in the page header.
            // Only a change of tenant earns the cover.
            if (next && next !== identity.communityId) setPendingId(next)
        }

        const stop = (): void => setPendingId(null)

        router.events.on('routeChangeStart', start)
        router.events.on('routeChangeComplete', stop)
        router.events.on('routeChangeError', stop)

        return () => {
            router.events.off('routeChangeStart', start)
            router.events.off('routeChangeComplete', stop)
            router.events.off('routeChangeError', stop)
        }
    }, [router.events, identity.communityId])

    // Belt and braces: if a page ever renders under a community the shell was
    // not expecting, the cover comes down rather than staying stuck over it.
    useEffect(() => {
        setPendingId((current) => (current === identity.communityId ? null : current))
    }, [identity.communityId])

    const pending = pendingId
        ? identity.switcher.find((entry) => entry.id === pendingId) ?? null
        : null

    const switching = pendingId !== null

    useEffect(() => {
        const saved = document.cookie
            .split('; ')
            .find((entry) => entry.startsWith(`${COOKIE}=`))

        if (saved?.endsWith('false')) setOpen(false)
    }, [])

    return (
        <TooltipProvider delay={120}>
            <SidebarProvider
                open={open}
                onOpenChange={setOpen}
                style={{ '--sidebar-width': '15rem', '--sidebar-width-icon': '3.25rem' } as CSSProperties}
                className='adm-scope h-svh min-h-svh overflow-hidden bg-adm-bg font-sans text-adm-ink'
            >
                <ConsoleSidebar identity={identity} pending={pending} />

                <SidebarInset className='relative flex h-svh min-w-0 flex-col overflow-hidden bg-adm-bg'>
                    <div
                        className='flex min-h-0 flex-1 flex-col'
                        inert={switching ? true : undefined}
                    >
                        {children}
                    </div>

                    {switching && (
                        <div
                            role='status'
                            aria-live='polite'
                            className='absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-adm-bg'
                        >
                            <Loader2
                                className='size-5 animate-spin text-adm-muted motion-reduce:animate-none'
                                aria-hidden='true'
                            />

                            <p className='px-6 text-center text-[13px] font-medium text-adm-ink'>
                                {pending ? `Opening ${pending.name}…` : 'Switching marketplace…'}
                            </p>

                            <p className='px-6 text-center text-[12px] text-adm-muted'>
                                Loading this marketplace&rsquo;s own data.
                            </p>
                        </div>
                    )}
                </SidebarInset>
            </SidebarProvider>
        </TooltipProvider>
    )
}
